// ABOUTME: Chapters whose point-tags are STATIC — fixed Figma copy shown for the whole hold,
// ABOUTME: bypassing the speech-driven matcher/dwell engine (client request 2026-09-04).

// EVERY tagged chapter is static as of 2026-09-04. The tags used to be driven by what the agent
// said: the matcher listened to the heard portion of a turn, resolved the named attribute off the
// product envelope and faded a plate in at the anchor for that key. The client's decision is that
// the reel should simply show the callouts the film was designed around, the way the exploded
// chapter already did since 2026-08-28 — so this table now covers `watch_360`, `dial`, `movement`,
// `exploded` and `crown`, i.e. every chapter Figma authors tags for.
//
// The speech-driven engine is NOT deleted. It stays exactly where it was (PointTagLayer's matcher
// effect, attributeMatcher.js, resolveTagContent.js, suppressRedundantTags.js, tagPlacement's
// region cascade) and is bypassed only because `staticTagsForChapter` answers for every chapter.
// Deleting a chapter's entry here hands it straight back to the dynamic path with no other change,
// which is the whole reason the two are kept as separate modules.
//
// ── WHERE THE COPY COMES FROM ───────────────────────────────────────────────
// The desktop frames were re-authored in Figma (`ABo9VOjbq9aZ9iz3OfncVV`, re-read 2026-09-04):
// every chapter's tags are now single-line 135.667-tall plates, several chapters gained tags they
// never had, and `movement` carries FIVE at once. Copy below is transcribed verbatim from those
// frames — see chapterTagAnchors.js for the per-tag node ids and coordinates.
//
// It is deliberately NOT resolved from product data. "Multilayered Dial", "Machine Chamfering" and
// "Independently Welded Teardrop Lug" appear in no Vesper catalogue record at all, which is exactly
// why the dynamic path could never render the design's own callouts.
//
// ── SPEC-54 COPY AUDIT, 2026-09-29 ───────────────────────────────────────────
// Every string below must be stated in Miguel's official Vesper deck (OPH-411; slide numbers in
// backend docs/brand/ophion/DATA-AUDIT-2026-09-29.md §2.4/§6.5), or be confirmed by the owner with
// Miguel (the base-calibre datasheet: 65 h power reserve, the VMF 5500 name). Five strings were neither — the old Figma
// copy still carried a leftover of the superseded Schwarz Etienne calibre and wording nobody
// sourced — and were REPLACED, text only: same tag count per chapter and breakpoint, same keys,
// same anchors and widths. A replacement is never longer than what it replaces, because a plate
// hugs its text up to the authored anchor width (desktop) and shares the row width with its
// neighbours (mobile), so a longer string would wrap or resize a box. A shorter one narrows its own
// DESKTOP plate (accepted by the owner, 2026-09-29), but on MOBILE the narrower box would let the
// row's neighbours drift, so a changed mobile tag carries `reserve` — the lines it replaced — which
// PointTag turns into an invisible, textless width floor (index.css `.tag-reserve`): the box stays
// exactly as wide as before while the new copy breaks on its own lines. `reserve` is layout data,
// never rendered as text. The keys keep their old names on purpose (`Escapement` now says "Machine
// Chamfering"): renaming needs a second anchor entry and touches every test that pins the key, for
// no visible change — the key is only ever a position name here.
//
// ── DESKTOP vs MOBILE ───────────────────────────────────────────────────────
// desktop — one line, so each entry is the frame's whole phrase carried as a label-less `value`.
//   No `variant` hierarchy hint: the frames no longer author a large/small split for it to pick.
// mobile  — the flat plate stacks its lines at one size, so each entry is an explicit `lines`
//   array. The line BREAKS are ours, not Figma's: the mobile frames were never part of the
//   2026-09-04 re-author (all five re-read that day and still showing the old two-tag prose rows
//   and, on exploded, the rejected "Guilloché"), and the client asked that mobile say what desktop
//   says. So mobile mirrors the desktop copy, re-broken to the mobile plate's width at a phrase
//   boundary — never stranding a lone adjective on a line.
// A tag with NO `mobile` entry is desktop-only: `movement` authors five plates over a 3840-wide
// frame and the mobile row physically holds three (mobileGeometry.TAG_ROW — movement's authored
// widths fill it exactly), so its two least product-defining specs are dropped there rather than
// squeezed in. That is the only place the two compositions differ in WHAT they say.
//
// Keys must have an authored anchor in chapterTagAnchors.js for the breakpoints they appear on, or
// the tag is dropped by the same no-guessed-positions rule as dynamic tags.
const STATIC_CHAPTER_TAGS = {
  // 45:1518 — three tags the frame did not carry before the re-author. Mobile 45:2834 authors NO
  // tag row here (only the product card), so this row is ours; the client asked for the three on
  // mobile too rather than a composition that silently drops the chapter's whole callout set.
  watch_360: [
    {
      key: 'Strap',
      desktop: { label: null, value: 'Leather Strap' },
      mobile: { lines: ['Leather', 'Strap'] },
    },
    {
      key: 'crystal',
      desktop: { label: null, value: 'Domed Sapphire / Double ARDUR Crystal' },
      // MOBILE ONLY (owner report 2026-09-15): re-wrapped from 3 lines to 2, matching its two
      // neighbours (`Strap`, `Lug_Construction`) — desktop's one-line box is untouched. A first
      // pass kept "Double ARDUR Crystal" as one line, but that phrase alone is too wide for the
      // row's per-tag share at 3 tags/row and wrapped a THIRD line in anyway (verified on the
      // `mobile-tags-watch_360` plate — the row visibly shrinks each box below its natural
      // content width once three of them compete for it, so a line has to fit the SHRUNK box,
      // not just look short on paper). "Double" is the modifier a mobile line can afford to drop.
      mobile: { lines: ['Domed Sapphire /', 'ARDUR Crystal'] },
    },
    {
      key: 'Lug_Construction',
      desktop: { label: null, value: 'Independently Welded Teardrop Lug' },
      // MOBILE ONLY (owner report 2026-09-15): same fix and same reason as `crystal` above —
      // "Welded" is the modifier a mobile line can afford to drop; "Independently" and "Teardrop
      // Lug" are the two words that actually identify the construction.
      mobile: { lines: ['Independently', 'Teardrop Lug'] },
    },
  ],
  // 45:1668 — the two editorial prose plates ("Inspired by Boullée's Newton Cenotaph.", "The
  // Vesper's dial reveals new depth with every glance.") are GONE from the desktop frame, replaced
  // by three spec callouts. Mobile 45:2947 still draws the prose pair; the client's instruction is
  // that mobile mirrors desktop, so the prose is retired on both.
  dial: [
    {
      key: 'Hands',
      // SPEC-54: was "Diamond Cut Rhodium-Plated Hands" — the deck says PVD-blued or nickel-plated,
      // diamond cut (slide 14), and this tag shows on every variant, so only "diamond-cut" is true
      // for all of them.
      desktop: { label: null, value: 'Diamond-Cut Hands' },
      // MOBILE ONLY (owner report 2026-09-15): re-wrapped from 3 lines to 2, matching its two
      // neighbours (`Dial_Finish`, `Edge_Finish`) — desktop's one-line box is untouched. A first
      // pass paired "Rhodium-Plated Hands" on one line, but that phrase alone is too wide for the
      // row's per-tag share at 3 tags/row and wrapped a THIRD line in anyway (same width-budget
      // issue as `watch_360`'s crystal/Lug_Construction tags — see their comments). "Rhodium-
      // Plated" is the modifier a mobile line can afford to drop here.
      mobile: { lines: ['Diamond Cut', 'Hands'] },
    },
    {
      key: 'Dial_Finish',
      desktop: { label: null, value: 'Polished 3D Micro-Indexes' },
      mobile: { lines: ['Polished 3D', 'Micro-Indexes'] },
    },
    {
      key: 'Edge_Finish',
      // SPEC-54: was "Polished with Chamfered Edges" — the chamfer is on the RINGS (deck slide 6:
      // "the 3 rings for the seconds and minutes are also raised, polished and chamfered"; slides
      // 7-8: "Chamfering rings"), not on unnamed edges.
      desktop: { label: null, value: 'Polished, Chamfered Rings' },
      mobile: { lines: ['Polished,', 'Chamfered Rings'], reserve: ['Polished with', 'Chamfered Edges'] },
    },
  ],
  // 45:2121 — FIVE plates. `Frequency` and `Escapement` are the two the mobile row cannot hold
  // (see the desktop-only note above); the three that stay are the three the mobile frame itself
  // chose back in Phase 4b, so the drop is the design's own selection rather than ours.
  movement: [
    {
      // SPEC-54: the deck does not state a power reserve; the source is the base-calibre datasheet
      // the owner adopted 2026-09-29 ("SEED VMF 5500/5501 automatic micro-rotor, power reserve 65
      // hours"). It replaced the old 86 h (the superseded ASE 200 figure) at the SAME length, so no
      // plate changes size and no `reserve` pin is needed.
      key: 'Power_Reserve',
      desktop: { label: null, value: '65 Hour Power Reserve' },
      mobile: { lines: ['65 Hour', 'Power Reserve'] },
    },
    {
      key: 'Movement',
      // Corrected 2026-09-15: the maker changed (client, written feedback 2026-09-08) — it is
      // Vaucher's 5500, not Schwarz Etienne's ASE 200 (written "MFV" until the 2026-09-29
      // datasheet, which names it VMF 5500). See CLIENT-FEEDBACK-2026-09-08-TRIAGE.md
      // #1 (backend repo). The Figma frame (45:2121, plate 369:113) still shows the old text —
      // flag to design so this stays in sync, this file is the one actually rendered.
      desktop: { label: null, value: 'Vaucher VMF 5500 Micro-Rotor' },
      // MOBILE ONLY (owner report 2026-09-15): re-wrapped from 3 lines to 2, matching its two
      // neighbours (`Power_Reserve`, `crystal`) — desktop's one-line box is untouched.
      mobile: { lines: ['Vaucher VMF 5500', 'Micro-Rotor'] },
    },
    // `Frequency` plate withdrawn 2026-09-15 — "28,800 vph" was authored for the superseded ASE
    // 200 and has no confirmed value for the Vaucher VMF 5500 (SPEC-54 AC-4, backend repo:
    // no unconfirmed sibling fact survives a movement-maker correction). The anchor at
    // chapterTagAnchors.js stays authored; this chapter simply draws one fewer plate there until
    // Miguel confirms a real frequency for the new calibre.
    {
      // SPEC-54: was "Swiss Lever Escapement" — not in Miguel's data. Deck slide 14: "machine
      // chamfering on the edges". Alternative of the same width if the plate turns out to sit over
      // the balance bridge: "Brushed Balance Bridge" ("machine straight brushing in the balance and
      // escapement bridge").
      key: 'Escapement',
      desktop: { label: null, value: 'Machine Chamfering' },
    },
    {
      key: 'crystal',
      // SPEC-54: was "Sapphire Display Window" — "display" is an inference. Deck slide 14:
      // "Bottom: flat sapphire crystal".
      desktop: { label: null, value: 'Flat Sapphire Caseback' },
      mobile: { lines: ['Flat Sapphire', 'Caseback'], reserve: ['Sapphire', 'Display Window'] },
    },
  ],
  // 45:1968 — the one chapter that was already static, left exactly as it shipped (client, 2026-09-04:
  // "exploded is already done and set"). The desktop frame has since nudged its three plates down and
  // left — (724,1205)/(1075,1397)/(1346,1589) against the shipped (890,1142)/(1300,1348)/(1810,1516) —
  // and that drift is knowingly NOT taken: the shipped placement is the one signed off.
  exploded: [
    {
      key: 'Hand_Style',
      desktop: { label: null, value: 'Diamond-Cut Hands' },
      mobile: { lines: ['Diamond-Cut', 'Hands'] },
    },
    {
      key: 'Dial_Finish',
      desktop: { label: null, value: '3D Micro Indexes' },
      mobile: { lines: ['3D Micro', 'Indexes'] },
    },
    {
      // The key is `Dial_Pattern`, not `Guilloche_*`: guilloché is a Vélos dial, not the Vesper's,
      // and the client has rejected the word twice. Mobile 45:3062 still draws "Guilloché wave
      // patterned dial" (re-checked 2026-09-04) — that frame simply was not part of the desktop
      // re-author, and following it would reinstate exactly the word that was rejected.
      key: 'Dial_Pattern',
      // SPEC-54: was "Geometric patterned dial" — a paraphrase of the design story, not a spec.
      // Deck slide 14: "Dial: Multilayered with laser cut microindexes".
      desktop: { label: null, value: 'Multilayered Dial' },
      mobile: { lines: ['Multilayered', 'Dial'], reserve: ['Geometric patterned', 'dial'] },
    },
  ],
  // 45:1818 — `Case_Finish` keeps its sentence; the "Every curve of the case is shaped by a shared
  // spherical geometry…" prose is replaced by two spec plates.
  crown: [
    {
      key: 'Case_Finish',
      desktop: { label: null, value: 'Stainless steel, hand-polished by a craftsman.' },
      // MOBILE ONLY (owner report 2026-09-15): the full sentence wrapped to 3 lines here, one more
      // than its two neighbours (`Case_Material`, `Case_Size`), so this plate stood visibly taller
      // in a row where the design intends all three the same size. Desktop's box already fits the
      // whole sentence on one line at 3840-wide, so its copy is untouched — this is a mobile-only
      // re-wrap of the SAME sentence into the same 2 lines its neighbours use, not a new attribute.
      mobile: { lines: ['Hand-polished', 'stainless steel.'] },
    },
    {
      key: 'Case_Material',
      desktop: { label: null, value: '316L Stainless Steel' },
      mobile: { lines: ['316L Stainless', 'Steel'] },
    },
    {
      key: 'Case_Size',
      desktop: { label: null, value: '39mm Case (38.5mm Bezel)' },
      mobile: { lines: ['39mm Case', '(38.5mm Bezel)'] },
    },
  ],
};

// Resolved once per (chapter, breakpoint) at module load rather than per call. `staticTagsForChapter`
// feeds a useEffect dependency array in PointTagLayer, so it has to return the SAME array reference
// on every render — building it on demand would re-admit every tag on every render.
const BY_BREAKPOINT = ['desktop', 'mobile'].reduce((acc, breakpoint) => {
  acc[breakpoint] = Object.fromEntries(
    Object.entries(STATIC_CHAPTER_TAGS).map(([chapterId, tags]) => [
      chapterId,
      tags.filter((tag) => tag[breakpoint]).map(({ key, ...rest }) => ({ key, content: rest[breakpoint] })),
    ]),
  );
  return acc;
}, {});

/**
 * The static tag set for a chapter on one composition, or null when the chapter's tags are
 * speech-driven. Entries are `{ key, content }`, where `content` is whatever PointTag consumes on
 * that composition — `{label, value}` on desktop, `{lines}` on mobile.
 *
 * A chapter in the table with no entries for THIS breakpoint answers with an empty array, not
 * null: it is still a static chapter, it just draws nothing here. Null is reserved for "this
 * chapter is speech-driven", and returning it would hand a composition back to the matcher that
 * the design has deliberately given no tags.
 */
export function staticTagsForChapter(chapterId, breakpoint = 'desktop') {
  return (BY_BREAKPOINT[breakpoint] || BY_BREAKPOINT.desktop)[chapterId] ?? null;
}
